import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { CurrencyProvider } from "@/context/CurrencyContext";

import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import VisitTracker from "@/components/VisitTracker";
import SplashScreen from "@/components/SplashScreen";
import WhatsAppWidget from "@/components/whatsapp/WhatsAppWidget";
import Footer from "@/components/Footer";
import InstallPrompt from "@/components/InstallPrompt";

import HomePage from "@/pages/HomePage";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CartPage from "@/pages/CartPage";
import AdminPage from "@/pages/AdminPage";
import NewProductsPage from "@/pages/NewProductsPage";
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import ContactPage from "@/pages/ContactPage";

function Protected({ children, adminOnly = false }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="label-kicker text-slate-500">Loading…</div>
            </div>
        );
    }
    if (user === false) return <Navigate to="/login" replace />;
    if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
    if (!adminOnly && user.role !== "admin" && user.status !== "approved") {
        return <Navigate to="/pending" replace />;
    }
    return children;
}

function PublicOnly({ children }) {
    const { user } = useAuth();
    if (user === null) return null;
    if (user && user !== false) {
        if (user.role === "admin") return <Navigate to="/admin" replace />;
        if (user.status === "approved") return <Navigate to="/" replace />;
        return <Navigate to="/pending" replace />;
    }
    return children;
}

function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <SiteContentProvider>
                    <CurrencyProvider>
                    <BrowserRouter>
                    <ScrollToTop />
                    <VisitTracker />
                    <SplashScreen />
                    <WhatsAppWidget />
                    <div className="App flex flex-col min-h-screen">
                        <Navbar />
                        <main className="flex-1">
                            <Routes>
                                <Route
                                    path="/login"
                                    element={
                                        <PublicOnly>
                                            <LoginPage />
                                        </PublicOnly>
                                    }
                                />
                                <Route
                                    path="/register"
                                    element={
                                        <PublicOnly>
                                            <RegisterPage />
                                        </PublicOnly>
                                    }
                                />
                                <Route path="/pending" element={<PendingApprovalPage />} />
                                <Route
                                    path="/"
                                    element={
                                        <Protected>
                                            <HomePage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/catalog"
                                    element={
                                        <Protected>
                                            <CategoryPage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/catalog/:categoryId"
                                    element={
                                        <Protected>
                                            <CategoryPage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/product/:productId"
                                    element={
                                        <Protected>
                                            <ProductDetailPage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/cart"
                                    element={
                                        <Protected>
                                            <CartPage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/new"
                                    element={
                                        <Protected>
                                            <NewProductsPage />
                                        </Protected>
                                    }
                                />
                                <Route
                                    path="/admin"
                                    element={
                                        <Protected adminOnly>
                                            <AdminPage />
                                        </Protected>
                                    }
                                />
                                <Route path="/contact" element={<ContactPage />} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </main>
                        <Footer />
                        <InstallPrompt />
                    </div>
                </BrowserRouter>
                </CurrencyProvider>
                </SiteContentProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
